import cloneDeep from 'lodash.clonedeep';
import {
    EditorMaskOverlay,
    EditorNonPrintableBackground,
    IBleed,
    ICoverPhotoMarginAttributes, IDatabaseBleed, IDatabaseEdits, IDatabasePage, IDatabaseProject,
    IEditorBackgroundElement, IEditorBleedProperties,
    IEditorClipartElement, IEditorEdge, IEditorEdits, IEditorEditsBleed, IEditorElement,
    IEditorElementError,
    IEditorFullPageElement, IEditorGraphicAsset,
    IEditorImage,
    IEditorImageElement,
    IEditorModel,
    IEditorPage, IEditorPhoto, IEditorPhotoElement,
    IEditorProject, IEditorTextElement,
    IEditorThumbSize,
    IEditorUtil, IEffects, IFreeObject, IImageEdits, IMaskBorder,
    IMaskPreview,
    Imports,
    INonPrintableBackgroundPreview,
    IPage,
    IPreviewThumb, IRedEye, IThumbDictionary,
    IWebsiteImage,
    IWebsiteThumb,
    MaskOverlayImage
} from "./editor.interfaces";
import {PageTypes} from "./models-base";
import {IPoint} from "fabric/fabric-impl";



/* tslint:disable no-invalid-this */

const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

const SF = globalImports.SF || (globalImports.SF = {});
SF.Util = SF.Util || {} as IEditorUtil;

// dependencies
const model: IEditorModel = globalImports.SF.Model;
const thumbSize: IEditorThumbSize = model.ThumbSize;
const productAttribute: IEditorBleedProperties = model.EditorBleedProperties;
const coverPhotoMarginAttribute: ICoverPhotoMarginAttributes = model.CoverPhotoMarginAttribute;

// define filters
const filters: string[] = ['none', 'black&white', 'duotone', 'sepia', 'autoCorrect',
        'clarendon', 'gingham', 'moon', 'lark', 'reyes', 'juno', 'slumber', 'crema', 'ludwig', 'perpetua',
        'aden', 'amaro', 'mayfair', 'rise', 'hudson', 'valencia', 'xpro2', 'sierra', 'lofi', 'hefe', 'nashville',
        'stinson', 'vesper', 'earlybird', 'brannan', 'sutro', 'toaster', 'walden', '1977', 'kelvin', 'maven',
        'ginza', 'skyline', 'dogpatch', 'brooklyn', 'helena', 'ashby', 'charmes'];

// get masks
const masks: string[] = Object.keys(globalImports.SF.Model.Masks);

export const SUPPORTED_FILTERS: string[] = ['none', 'clarendon', 'gingham', 'moon', 'reyes', 'juno', 'aden', 'rise', 'sutro', 'toaster', 'kelvin', 'black&white'];

export const FILTER_TO_NAME: {[key:string]:string} = {
    none: 'None',
    clarendon: 'Yonath',
    gingham: 'Wescoff',
    moon: ' Tereshkova',
    lark: ' Roentgen',
    reyes: 'Nightingale',
    juno: ' Margulis',
    slumber: 'Leavitt',
    crema: 'Poincare',
    ludwig: 'Noyce',
    aden: ' Kowalevski',
    perpetua: 'Hypatia',
    amaro: 'Goldstine',
    mayfair: 'Davinci',
    rise: ' Cartwright',
    hudson: 'Blackwell',
    valencia: 'Bardeen',
    xpro2: 'Diffie',
    sierra: 'Mayer',
    willow: 'Northcutt',
    lofi: ' Raman',
    hefe: ' Lewin',
    nashville: 'Feynman',
    stinson: 'Stinson',
    vesper: 'Chatterjee',
    earlybird: 'Brattain',
    brannan: 'Almeida',
    sutro: 'Haslett',
    toaster: 'Kepler',
    walden: 'Turing',
    nineteen: 'Pare',
    kelvin: 'Mendel',
    maven: 'Kirch',
    ginza: 'Knuth',
    skyline: 'Hodgkin',
    dogpatch: 'Shockley',
    brooklyn: 'Wiles',
    helena: 'Meitner',
    ashby: 'Curran',
    charmes: 'Dewdney',
    'black&white': 'Nostalgia'
};

export class EditorEdits implements IEditorEdits {

    private canCreateArt:boolean = false;

    public setCanCreateArt(canCreateArt: boolean): void {
        this.canCreateArt = canCreateArt;
    }

    public toProject(data: IDatabaseProject): IEditorProject {
        const project:IEditorProject = new model.Project();
        project.name = data.name;
        project.id = data.photo_project_id;
        project.productId = data.product_id;
        project.productTypeId = data.product_type_id;
        project.photobookId = data.photobook_id;
        project.photoId = data.photo_id || undefined;
        project.cartItemId = data.cart_item_id;
        project.productDesignId = data.theme_id;    // todo: change this data coming from the server...
        project.schemaName = data.schema_name;
        project.classification = data.classification;
        project.productType = data.type;
        project.productName = data.product_name;
        project.productSchemaName = data.schema_name;
        project.editorType = data.sub_type;
        project.previewData = data.preview_data;
        project.themeId = data.theme_id;
        project.hasSelectableAttributes = data.has_selectable_attributes;
        project.hasSelectableWrapStyle = data.has_selectable_wrap_style;
        project.product = new model.Product(data.product_id, data.price, Object.assign({}, data.product_sku_data, data.dynamic_data), data.page_names);

        // project photos
        project.photos = new model.ProjectPhotos(data.photos && data.photos.used ? data.photos.used : []);

        // convert pages
        project.initializePages(data.page_list.map(page => this.toPage(page, data.page_names, page.edits.bleed)));
        project.pages.forEach((page: IEditorPage) => {
            if (page.type === model.PageTypes.FRONT_COVER || page.type === model.PageTypes.BACK_COVER || page.type === model.PageTypes.SPINE) {
                this.setCoverPageBleed(page.bleed, page);
            }
        });
        project.pageNames = data.page_names || [];
        project.selectedPageIndex = parseInt(data.selected_page_index as any, 10);

        // convert editor settings
        project.settings = new model.EditorSettings(data.settings, false);
        project.settings.setTemplateHasNoPhotos(!this.hasEditablePhoto(data.page_list));

        const imageScale = Math.max(project.pages[0].width, project.pages[0].height) /
            Math.max(parseInt(data.dynamic_data.optimal_x, 10), parseInt(data.dynamic_data.optimal_y, 10)) || 1;

        //error and warning dpi settings
        project.errorConfiguration = {
            zoomWarning: parseInt(data.dynamic_data.optimal_d, 10) / parseInt(data.dynamic_data.warning_d, 10) * imageScale || 1.5,
            zoomError: parseInt(data.dynamic_data.optimal_d, 10) / parseInt(data.dynamic_data.minimum_d, 10) * imageScale || 2
        };

        // project keyhole
        let keyholePositionLeft: number = 0;
        let keyholePositionTop: number = 0;

        if (data.dynamic_data.keyhole_position_l && data.dynamic_data.keyhole_position_t) {
            keyholePositionLeft = parseInt(data.dynamic_data.keyhole_position_l, 10);
            keyholePositionTop = parseInt(data.dynamic_data.keyhole_position_t, 10);
        }
        if (parseInt(data.dynamic_data.has_keyhole, 10) == 1) {
            project.keyhole = {
                keyholePositionLeft: keyholePositionLeft,
                keyholePositionTop: keyholePositionTop,
                keyholeSizeHeight: parseInt(data.dynamic_data.keyhole_size_h, 10),
                keyholeSizeWidth: parseInt(data.dynamic_data.keyhole_size_w, 10),
                keyholeSvgString: data.dynamic_data.keyhole_svg_string
            };
            project.hasKeyhole = true;
        } else {
            project.hasKeyhole = false;
        }
        if (data.mask_preview_data) {
            const empty : MaskOverlayImage = {
                image: null,
                margin_bottom: 0,
                margin_top: 0,
                margin_left: 0,
                margin_right: 0
            };
            if (data.mask_preview_data.mask) {
                const overlayPreview: EditorMaskOverlay = {
                    imageLandscape: structuredClone(empty),
                    imagePortrait: structuredClone(empty)
                };
                const overlayLandscape: IMaskPreview|undefined    = data.mask_preview_data.mask.L;
                const overlayPortrait: IMaskPreview|undefined = data.mask_preview_data.mask.P;
                if (overlayLandscape && overlayPreview.imageLandscape) {
                    const overlayImageLandscape: IWebsiteImage = this.buildOverlayMaskPreview(overlayLandscape);
                    overlayPreview.imageLandscape.image = globalImports.SF.Util.Translator.toImageModel(overlayImageLandscape, '');
                    this.getMargins(overlayPreview.imageLandscape, overlayLandscape);
                }
                if (overlayPortrait && overlayPreview.imagePortrait) {
                    const overlayImagePortrait: IWebsiteImage = this.buildOverlayMaskPreview(overlayPortrait);
                    overlayPreview.imagePortrait.image = globalImports.SF.Util.Translator.toImageModel(overlayImagePortrait, '');
                    this.getMargins(overlayPreview.imagePortrait, overlayPortrait);
                }

                if (overlayPreview.imageLandscape && overlayPreview.imageLandscape.image && overlayPreview.imagePortrait && !overlayPreview.imagePortrait.image) {
                    overlayPreview.imagePortrait.image = overlayPreview.imageLandscape.image;
                    this.getMargins(overlayPreview.imagePortrait, overlayLandscape as IMaskPreview );
                } else if (overlayPreview.imagePortrait && overlayPreview.imagePortrait.image && overlayPreview.imageLandscape && !overlayPreview.imageLandscape.image) {
                    overlayPreview.imageLandscape.image =  overlayPreview.imagePortrait.image;
                    this.getMargins(overlayPreview.imageLandscape, overlayPortrait as IMaskPreview);
                }
                project.hasOverlayPreview = true;
                project.overlayPreview = overlayPreview;
            }

            if (data.mask_preview_data.mask_bw) {
                const editorMaskBW: EditorMaskOverlay = {
                    imageLandscape: structuredClone(empty),
                    imagePortrait: structuredClone(empty)
                };

                const maskLandscape: IMaskPreview|undefined = data.mask_preview_data.mask_bw.L;
                const maskPortrait: IMaskPreview|undefined = data.mask_preview_data.mask_bw.P;
                if (maskLandscape && editorMaskBW.imageLandscape) {
                    const maskImageLandscape: IWebsiteImage = this.buildOverlayMaskPreview(maskLandscape);
                    editorMaskBW.imageLandscape.image = globalImports.SF.Util.Translator.toImageModel(maskImageLandscape, '');
                    this.getMargins(editorMaskBW.imageLandscape, maskLandscape);

                }
                if (maskPortrait && editorMaskBW.imagePortrait) {
                    const maskImagePortrait: IWebsiteImage = this.buildOverlayMaskPreview(maskPortrait);
                    editorMaskBW.imagePortrait.image = globalImports.SF.Util.Translator.toImageModel(maskImagePortrait, '');
                    this.getMargins(editorMaskBW.imagePortrait, maskPortrait);
                }

                if (editorMaskBW.imageLandscape &&  editorMaskBW.imageLandscape.image
                        && editorMaskBW.imagePortrait && !editorMaskBW.imagePortrait.image) {
                    editorMaskBW.imagePortrait.image = editorMaskBW.imageLandscape.image;
                    this.getMargins(editorMaskBW.imagePortrait, maskLandscape as IMaskPreview);
                } else if (editorMaskBW.imagePortrait &&  editorMaskBW.imagePortrait.image
                        && editorMaskBW.imageLandscape && !editorMaskBW.imageLandscape.image) {
                    editorMaskBW.imageLandscape.image =  editorMaskBW.imagePortrait.image ;
                    this.getMargins(editorMaskBW.imageLandscape, maskPortrait as IMaskPreview);
                }

                project.maskBW = editorMaskBW;
            } else if (data.mask_preview_data.mask_merge) {
                const maskMerge:{[key:string]:MaskOverlayImage} = {};
                Object.keys(data.mask_preview_data.mask_merge).forEach(key => {
                    if (data.mask_preview_data.mask_merge && data.mask_preview_data.mask_merge[key]) {
                        maskMerge[key] = structuredClone(empty);
                        maskMerge[key].image = globalImports.SF.Util.Translator.toImageModel(
                            this.buildOverlayMaskPreview(data.mask_preview_data.mask_merge[key]), '');
                        this.getMargins(maskMerge[key], data.mask_preview_data.mask_merge[key]);
                    }
                });
                project.maskMerge = maskMerge;
            }

        }

        if (data.non_printable_background_preview_data) {
            const nonPrintableBackground = data.non_printable_background_preview_data.non_printable_background;
            if (nonPrintableBackground) {
                const editorNonPrintableBackground: EditorNonPrintableBackground = {
                    imageLandscape: {
                        image: null,
                    },
                    imagePortrait: {
                        image: null,
                    }
                };
                const nonPrintableBackgroundLandscape: INonPrintableBackgroundPreview|undefined = nonPrintableBackground.L;
                const nonPrintableBackgroundPortrait: INonPrintableBackgroundPreview|undefined = nonPrintableBackground.P;
                if (nonPrintableBackgroundLandscape && editorNonPrintableBackground.imageLandscape) {
                    const nonPrintableBackgroundImageLandscape: IWebsiteImage = this.buildNonPrintableBackgroundPreview(nonPrintableBackgroundLandscape);
                    editorNonPrintableBackground.imageLandscape.image = globalImports.SF.Util.Translator.toImageModel(nonPrintableBackgroundImageLandscape, '');

                }
                if (nonPrintableBackgroundPortrait && editorNonPrintableBackground.imagePortrait) {
                    const nonPrintableBackgroundImagePortrait: IWebsiteImage = this.buildNonPrintableBackgroundPreview(nonPrintableBackgroundPortrait);
                    editorNonPrintableBackground.imagePortrait.image = globalImports.SF.Util.Translator.toImageModel(nonPrintableBackgroundImagePortrait, '');
                }

                if (editorNonPrintableBackground.imageLandscape &&  editorNonPrintableBackground.imageLandscape.image && editorNonPrintableBackground.imagePortrait && !editorNonPrintableBackground.imagePortrait.image) {
                    editorNonPrintableBackground.imagePortrait.image = editorNonPrintableBackground.imageLandscape.image;
                } else if (editorNonPrintableBackground.imagePortrait &&  editorNonPrintableBackground.imagePortrait.image && editorNonPrintableBackground.imageLandscape && !editorNonPrintableBackground.imageLandscape.image) {
                    editorNonPrintableBackground.imageLandscape.image =  editorNonPrintableBackground.imagePortrait.image ;
                }

                project.hasNonPrintableBackground = true;
                project.nonPrintableBackground = editorNonPrintableBackground;
            }

        }

        project.initializeErrors();
        // init
        project._resetPhotoUsage();
        project.hasCoverPageBleed = this.hasCoverPageBleed(project.pages);
        return project;
    }

    public getLabel(type: string, pageNumber:number, pageNames:string[]|undefined, forPager:boolean):string {
        let label = '';
        if (pageNames && pageNames[pageNumber - 1]) {
            return pageNames[pageNumber - 1];
        }

        if (typeof PageTypes === 'undefined') {
            // when not using the Editor UI, PageTypes may be unset.  this is true when rendering via JRS.
            // if this is unset, just return whatever; no one cares; the label is never shown in this case anyway.
            // allowing this method to proceed with unset PageTypes will CRASH THE JRS.
            // even if we could get PageTypes, it is likely in this case that SF.tr is also undefined.
            // just say no.
            return type;
        }

        switch (type) {
            case PageTypes.PAGE:
                if (forPager) {
                    label = pageNumber.toString();
                } else {
                    label = SF.tr('PAGER_PAGE_X', {PAGE: pageNumber}, 'Page {{PAGE}}');
                }
                break;

            case PageTypes.FRONT_COVER:
                if (pageNames && pageNames[pageNames.length - 1]) {
                    label = pageNames[pageNames.length - 1];
                } else {
                    label = SF.tr('PAGER_FRONT_COVER_SHORT', {}, 'Front');
                }
                break;

            case PageTypes.BACK_COVER:
                label = SF.tr('PAGER_BACK_COVER_SHORT', {}, 'Back');
                break;

            case PageTypes.SPINE:
                if (forPager) {
                    label = '';
                } else {
                    label = SF.tr('PAGER_SPINE', {}, 'Spine');
                }
                break;

            case PageTypes.FRONT_FLY:
                label = SF.tr('PAGER_FRONT_FLY_SHORT', {}, 'Inside');
                break;

            case PageTypes.BACK_FLY:
                label = SF.tr('PAGER_BACK_FLY_SHORT', {}, 'Inside');
                break;

            default:
                console.warn('Invalid type: ' + type);
        }

        return label;
    }

    public toPage(data: IPage, pageNames: string[]|undefined, bleedData: IDatabaseBleed | null = null, isProductionImage: boolean = false): IEditorPage {
        const page = new model.Page();
        page.id = data.page_id;
        page.number = parseInt(data.page_number as any, 10);
        page.type = data.edits.type;
        page.label = this.getLabel(page.type, page.number, pageNames, false);
        page.pagerLabel = this.getLabel(page.type, page.number, pageNames, true);
        page.width = parseInt(data.edits.width as any, 10);
        page.height = parseInt(data.edits.height as any, 10);
        page.canEdit = !!data.edits.canEdit;
        page.canReorder = !!data.edits.canReorder;
        page.canDelete = !!data.edits.canDelete;
        page.joined =!! data.edits.joinedPage;
        page.elements = this.toElements(data.edits.freeObject);
        page.preview = data.preview_image;
        page.bleed = this.convertToBleed(data.edits.bleed, page);
        page.duplicateId = null;
        page.productType = data.edits.productType;
        if (model && (page.type === model.PageTypes.FRONT_COVER || page.type === model.PageTypes.BACK_COVER || page.type === model.PageTypes.SPINE) && bleedData) {
            page.height += (bleedData.coverPhotoMarginBottom || 0)
            + (bleedData.coverPhotoMarginTop || 0);
            if (isProductionImage && (page.type === model.PageTypes.FRONT_COVER || page.type === model.PageTypes.BACK_COVER)) {
                page.width += (bleedData.coverPhotoMarginRight || 0) + (bleedData.coverPhotoMarginLeft || 0);
            } else {
                if (page.type === model.PageTypes.FRONT_COVER) {
                    page.width += bleedData.coverPhotoMarginRight || 0;
                } else if (page.type === model.PageTypes.BACK_COVER) {
                    page.width += bleedData.coverPhotoMarginLeft || 0;
                }
            }
        }

        return page;
    }

    public setCoverPageBleed(bleedData: IBleed, page: IEditorPage): void {
        if (page.type === model.PageTypes.FRONT_COVER || page.type === model.PageTypes.BACK_COVER || page.type === model.PageTypes.SPINE) {
            if (!page.bleed) {
                page.bleed = {};
            }
            page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM] = bleedData.coverPhotoMarginBottom || 0;
            page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP] = bleedData.coverPhotoMarginTop || 0;
            page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT] = bleedData.coverPhotoMarginLeft || 0;
            page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT] = bleedData.coverPhotoMarginRight || 0;
        }
    }

    public hasCoverPageBleed(pages: IEditorPage[]): boolean {
        const coverPageWithBleed = pages.find((page: IEditorPage) => {
            return ((page.type === model.PageTypes.FRONT_COVER || page.type === model.PageTypes.BACK_COVER || page.type === model.PageTypes.SPINE)
                && (page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT]
                    || page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT]
                    || page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM]
                    || page.bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP]));
        });
        return coverPageWithBleed !== undefined;
    }

    public validatePage(data:IPage):boolean {
        if (data.page_id && parseFloat(data.page_number as any) > 0 &&  parseFloat(data.edits.width as any) > 0 &&
            parseFloat(data.edits.height as any) > 0 &&  data.edits.type) {

            if (Array.isArray( data.edits.freeObject)) {
                // if any of the data is bad, the page is bad...
                return !data.edits.freeObject.some((freeObject:IFreeObject):boolean => {
                    try {
                        const el: IEditorElement|null = this.toElement(freeObject);

                        // do some filtering for bad data
                        return (!el || (el instanceof (model.ClipartElement as any) && (el as IEditorClipartElement).image === null));
                    } catch (e) {
                        console.error(e);
                        return true;
                    }
                });

            }

        }
        return false;
    }

    public toElements(freeObjects: IFreeObject[]): IEditorElement[] {
        const elements: IEditorElement[] = [];
        for (let i = 0; i < freeObjects.length; i++) {
            const el: IEditorElement|null = this.toElement(freeObjects[i]);

            // do some filtering for bad data that sometimes comes from flash
            if (el instanceof (model.ClipartElement as any) && (el as IEditorClipartElement).image === null) {
                continue;
            }

            // for some reason, some backgrounds are not the first element
            if (freeObjects[i].isBackground && i > 0 && el) {
                elements.unshift(el);
            } else if (el) {
                // add element
                elements.push(el);
            } else {
                console.warn('Unable to convert freeObejct to element', freeObjects[i]);
            }
        }

        return elements;
    }

    public toElement(freeObject: IFreeObject): IEditorElement|null {
        let element: IEditorElement|null;
        let effects: any = {};

        // convert shared effects here

        // convert to element
        if (freeObject.textEdits) {
            element = this.getTextEditsElement(freeObject);
            element.textZoneId = freeObject.textZoneId || undefined;
            element.linkedZoneId = freeObject.linkedZoneId || undefined;
        } else {
            // it's an image
            const image:IEditorImage|null = this.createImageModelFromFreeObject(freeObject);

            // image edits
            const imageEdits = freeObject.imageEdits;

            // image effects
            if (imageEdits && image) {
                effects = this.getEffects(imageEdits, image);
            }

            if (freeObject.isClipArt && image) {
                element = new model.ClipartElement(image);
            } else {
                element = this.getNonClipArtImage(imageEdits, freeObject, effects.rotation, image);
            }
        }

        if (!element) {
            return null;
        }

        // setup basic element data
        if (freeObject.freeObjectId !== undefined) {
            element.freeObjectId = freeObject.freeObjectId;
        }
        if (freeObject.fillColor) {
            element.fillColor = freeObject.fillColor;
        }
        if(freeObject.context) {
            element.context = freeObject.context;
        }
        if (freeObject.skipSelectEvent) {
            element.skipSelectEvent = freeObject.skipSelectEvent;
        }
        if (freeObject.pattern) {
            element.pattern = freeObject.pattern;
        }
        if (freeObject.scaleX !== undefined) {
            element.scaleX = freeObject.scaleX;
        }
        if (freeObject.scaleY !== undefined) {
            element.scaleY = freeObject.scaleY;
        }
        element.x = parseFloat(freeObject.x.toString());
        element.y = parseFloat(freeObject.y.toString());
        element.width = parseFloat(freeObject.width.toString());
        element.height = parseFloat(freeObject.height.toString());
        element.rotation = freeObject.rotation;
        element.opacity = freeObject.alpha !== undefined ? freeObject.alpha : 1; // freeObject.alpha can be zero
        element.effects = effects;

        // Allow those creating art to edit uneditable elements, but then restore them when saving back to the database (see fromElement)
        if (this.canCreateArt) {
            element.editable = true;
            element.editableSave = freeObject.canEdit;
        } else {
            element.editable = freeObject.canEdit;
        }
        element.locked = freeObject.locked;
        element.fixed = freeObject.fixed;
        element.isLetterBoxed = !!freeObject.isLetterBoxed;
        element.order = freeObject.order;
        this.setPlatformSpecificDataOnElement(element, freeObject);
        if ((element as IEditorFullPageElement).imagePosition) {
            (element as any).userZoom = (element as IEditorFullPageElement).imagePosition.zoom;
        }
        if ((freeObject as any).uniquePhotoId) {
            (element as any).uniquePhotoId = (freeObject as any).uniquePhotoId;
        }

        // copy over effects that we don't support yet
        if (freeObject.glowShadowReflection) {
            this.parseGlowShadowReflection(element, freeObject.glowShadowReflection);
        }

        // deal with mask and borders
        if (freeObject.maskBorder) {
            this.parseMaskBorder(element, freeObject.maskBorder);
        }

        // deal with vignette
        if (freeObject.vignette) {
            effects.vignetteColor = this.parseColor(freeObject.vignette.color);
            effects.vignetteBlur = freeObject.vignette.blur;
            effects.vignetteOpacity = freeObject.vignette.alpha * 100;
        }
        if ((freeObject as any).mainType && (freeObject as any).mainType == 'shapeAsset') {
            if ((freeObject as any).border) {
                effects.borderColor = this.parseColor((freeObject as any).border.color);
                effects.borderSize = (freeObject as any).border.size;
            }
        }

        return element;
    }

    protected setPlatformSpecificDataOnElement(element: IEditorElement, freeObject: IFreeObject): void {
        // do nothing
        // should be overridden when needed
    }

    protected createImageModelFromFreeObject(freeObject: IFreeObject): IEditorImage|null {
        let image: IEditorImage|null;
        if (freeObject.photoId && freeObject.photoId != -1) {
            image = this.getPhotoModel(freeObject.photoId, freeObject.imageEdits);
        } else if (freeObject.isClipArt && freeObject.graphicAssetId) {
            image = this.getClipArtModel(freeObject);
        } else if (freeObject.isBackground && freeObject.graphicAssetId) {
            image = this.getBackgroundModel(freeObject.graphicAssetId, freeObject.imageEdits);
        } else {
            // @note: covers and spine don't have an ID, figure it out later.
            image = this.getImageModel(null, freeObject.imageEdits);
        }

        return image;
    }

    private getNonClipArtImage(imageEdits: IImageEdits|undefined, freeObject: IFreeObject, rotation: number|undefined, image: IEditorImage|null):IEditorElement|null {
        // backgrounds and photos require imagePosition data
        let imagePosition;
        let element:IEditorElement|null = null;

        // sometimes we have image edits, but without an actual image..
        if (imageEdits && imageEdits.fullImageWidth) {
            const zoom = imageEdits.zoom / 100;
            imagePosition = {
                x: imageEdits.picturePosX,
                y: imageEdits.picturePosY,
                width: imageEdits.fullImageWidth,
                height: imageEdits.fullImageHeight,
                zoom: zoom
            };
        } else {
            imagePosition = {
                x: freeObject.width / -2,
                y: freeObject.height / -2,
                width: freeObject.width,
                height: freeObject.height,
                zoom: 1
            };
        }

        // if we have a rotation, we need to rotate the imagePosition
        if (rotation) {
            SF.Util.Helpers.rotateImagePosition(imagePosition, rotation);
        }

        if ((freeObject as any).type && (freeObject as any).type == 'group') {
            const freeObjects = (freeObject as any)._objects;
            const innerObject: IEditorElement[] = [];
            for (let i = 0; i < freeObjects.length; i++) {
                const item = this.toElement(freeObjects[i]);
                if (item) {
                    innerObject.push(item);
                } else {
                    console.warn('Unable to convert to element: ' + JSON.stringify(freeObjects[i]));
                }
            }

            element = new model.GroupElement(freeObject, innerObject, (freeObject as any)._canvas);
        }
        if ((freeObject as any).mainType && (freeObject as any).mainType == 'shapeAsset') {
            element = freeObject as any;
        }

        // TODO: This is pretty sktchy, if element is null bad things happen.
        if (freeObject.isBackground && image) {
            element = new model.BackgroundElement(image, imagePosition);
            (element as IEditorBackgroundElement).onSpine = !!freeObject.onSpine;
        } else if (freeObject.isForeground && image) {
            element = new model.ForegroundElement(image, imagePosition);
        } else if (!(freeObject as any).mainType && (freeObject as any).type != 'group') {
            element = new model.PhotoElement(image as IEditorImage, imagePosition, this.getEdgeAsImageModel(freeObject.edgeId || 0, freeObject) || undefined);
        }
        if (!element) {
            throw new Error('Unable to create for non-clip-art image');
        }
        return element;
    }

    private getEffects(imageEdits: IImageEdits, image: IEditorImage):IEffects {
        return {
            brightness: imageEdits.brightness,
            contrast: imageEdits.contrast,
            saturation: imageEdits.saturation,
            luminance: imageEdits.luminance,
            hue: imageEdits.hue,
            red: imageEdits.red,
            green: imageEdits.green,
            blue: imageEdits.blue,
            sharpness: imageEdits.sharpness,
            flipHorizontal: imageEdits.flipHorizontal,
            flipVertical: imageEdits.flipVertical,
            initialRotation: imageEdits.initialRotation || 0,
            rotation: imageEdits.rotation + (imageEdits.exifRotation || 0) || 0,
            filter: this.getFilter(imageEdits.colorMode) || undefined,
            autoCorrect: imageEdits.autoCorrect,
            autoRedEye: imageEdits.autoRedEye,
            duotoneColor: imageEdits.duotoneColor,
            redEyes: this.changeRedEyeOriginToCenter(imageEdits, image)
        };
    }

    protected getTextEditsElement(freeObject: IFreeObject):IEditorTextElement {
        let element: IEditorTextElement;
        if (!freeObject.textEdits) {
            throw new Error('text element without freeObject.textEdits');
        }
        if (freeObject.textEdits.text && freeObject.textEdits.rootStyle && freeObject.textEdits.styles) {
            element = new model.TextElement(freeObject.textEdits.text, freeObject.textEdits.rootStyle, freeObject.textEdits.styles, freeObject.textEdits);
        } else  {
            const textData = freeObject.textEdits.TLFText ? globalImports.SF.Util.TLF.toData(freeObject.textEdits.TLFText) : freeObject.textEdits;

            // some old projects had fontSizes in strings, cleanup here
            if (textData.rootStyle && textData.rootStyle.fontSize && (typeof textData.rootStyle.fontSize as unknown) === 'string') {
                textData.rootStyle.fontSize = parseInt(textData.rootStyle.fontSize as unknown as string, 10);
            }

            // create element
            element = new model.TextElement(textData.text as string, textData.rootStyle, textData.styles, freeObject.textEdits);
            element.tlf = freeObject.textEdits.TLFText;
        }
        element.preview_image = freeObject.textEdits.preview_image;
        element.preview_file = freeObject.textEdits.preview_file;
        element.edited = freeObject.textEdits.edited;
        if ((freeObject.textEdits as any).addedByUser !== undefined) {
            element.addedByUser = (freeObject.textEdits as any).addedByUser;
        }
        element.editable = freeObject.canEdit;
        element.customerEditable = freeObject.customerEditable;
        element.originalHeight = freeObject.textEdits.originalHeight;
        if (freeObject.textEdits.placeHolderTranslations) {
            element.placeHolderTranslations = freeObject.textEdits.placeHolderTranslations;
        }
        return element;
    }

    /////////////////////////////////////////////////////
    // UTILITY FUNCTIONS TO CONVERT TO DATABASE FORMAT //
    /////////////////////////////////////////////////////
    public fromPage(page: IEditorPage, canCreateArt: boolean, outputFormat: string): IDatabasePage {
        return {
            edits: this.convertToDatabaseEdits(page, canCreateArt),
            page_id: page.id || 0,
            preview_image: page.isPreviewPage && page.preview ? page.getPreviewImageAsBase64(outputFormat) : null,
            page_number: page.number
        };
    }

    public fromElement(element: any): IFreeObject {
        const freeObject: any = {
            x: element.x,
            y: element.y,
            width: element.width,
            height: element.height,
            rotation: element.rotation,
            alpha: element.opacity,
            canEdit: element.editable,
            locked: element.locked,
            fixed: element.fixed,
            isLetterBoxed: element.isLetterBoxed,
            order: element.order,
            uniquePhotoId: element.uniquePhotoId
        };

        // Allow those creating art to edit uneditable elements (see toElement), but then restore them when saving back to the database
        if (this.canCreateArt && typeof element.editableSave !== 'undefined') {
            freeObject.canEdit = element.editableSave;
        }

        if (element.freeObjectId !== undefined) {
            freeObject.freeObjectId = element.freeObjectId;
        }

        if (element.fillColor) {
            freeObject.fillColor = element.fillColor;
        }
        if (element.context) {
            freeObject.context = element.context;
        }
        if (element.skipSelectEvent) {
            freeObject.skipSelectEvent = element.skipSelectEvent;
        }
        if (element.pattern !== undefined) {
            freeObject.pattern = element.pattern;
        }
        if (element.scaleX !== undefined) {
            freeObject.scaleX = element.scaleX;
        }
        if (element.scaleY !== undefined) {
            freeObject.scaleY = element.scaleY;
        }

        const effects:IEffects = element.effects;

        // if we have a text element
        if (element instanceof model.TextElement) {
            const textEdits: any = {};
            /* We can remove tlftext from edits as we are not using it to create fabricjs text. The required styles are saved in styles property which is used by fabric to create text object.*/
            // textEdits.TLFText = SF.Util.TLF.toTLF(element);
            textEdits.rootStyle = element.rootStyle;
            // Get rid of empty objects in element.styles when saving to edits
            textEdits.styles = this.convertToDatabaseTextStyles(element.styles);
            textEdits.text = element.text;
            textEdits.preview_image = element.preview_image;
            textEdits.preview_file = element.preview_file;
            textEdits.edited = element.edited;
            if (element.addedByUser !== undefined) {
                textEdits.addedByUser = element.addedByUser;
            }
            textEdits.originalHeight = element.originalHeight;
            if ((element as any).placeHolderTranslations) {
                textEdits.placeHolderTranslations = (element as any).placeHolderTranslations;
            }
            freeObject.customerEditable = element.customerEditable;
            freeObject.linkedZoneId = element.linkedZoneId;
            freeObject.textZoneId = element.textZoneId;
            freeObject.textEdits = textEdits;
            // freeObject.canEdit = element.editable;
        } else {
            // otherwise we have an image
            const imageEdits: any = {};

            // if the image is actually set
            if (element.image) {
                // retain asset id's
                if (element.image instanceof model.GraphicAsset) {
                    freeObject.graphicAssetId = element.image.id;
                } else if (element.image instanceof model.Photo) {
                    freeObject.photoId = element.image.id;
                }

                // copy the data over
                // if it's a clipart, it belongs in the frame data
                // noinspection SuspiciousInstanceOfGuard
                if (element instanceof model.ClipartElement) {
                    const clipArtElement: IEditorClipartElement = element as IEditorClipartElement;
                    if (clipArtElement.image) {
                        freeObject.urlPrefix = clipArtElement.image.urlPrefix;
                        freeObject.pathPrefix = clipArtElement.image.pathPrefix;

                        this.copyImageData(clipArtElement.image, thumbSize.FULL, freeObject, 'fullFrame');
                        this.copyImageData(clipArtElement.image, thumbSize.LARGE, freeObject, 'largeFrame');
                        this.copyImageData(clipArtElement.image, thumbSize.MEDIUM, freeObject, 'mediumFrame');
                        this.copyImageData(clipArtElement.image, thumbSize.SMALL, freeObject, 'smallFrame');
                    } else {
                        console.error('Missing image in clipart');
                    }
                } else {
                    // since it is not a clipart, we also have positioning data
                    // that said, we need to counter rotate the data first
                    if (element.effects && element.effects.rotation) {
                        SF.Util.Helpers.rotateImagePosition(element.imagePosition, -element.effects.rotation);
                    }
                    if (element.image.exifRotation) {
                        imageEdits.exifRotation = element.image.exifRotation;
                    }
                    imageEdits.picturePosX = element.imagePosition.x;
                    imageEdits.picturePosY = element.imagePosition.y;
                    this.setFullImageDimension(imageEdits, element);
                    imageEdits.zoom = element.imagePosition.zoom * 100;
                    imageEdits.urlPrefix = element.image.urlPrefix;
                    imageEdits.pathPrefix = element.image.pathPrefix;

                    if(element.image.autoCorrectImageFilename) {
                        imageEdits.autoCorrectImageFilename = element.image.autoCorrectImageFilename;
                    }

                    this.copyImageData(element.image, thumbSize.FULL, imageEdits, 'fullImage');
                    this.copyImageData(element.image, thumbSize.XLARGE, imageEdits, 'xlargeImage');
                    this.copyImageData(element.image, thumbSize.LARGE, imageEdits, 'largeImage');
                    this.copyImageData(element.image, thumbSize.MEDIUM, imageEdits, 'mediumImage');
                    this.copyImageData(element.image, thumbSize.SMALL, imageEdits, 'smallImage');
                }
            }

            // if we have a photo, it has an edge and potentially different frame information
            if (element instanceof model.PhotoElement && element.frame) {
                // add frame details -- in the flash editor, the clipart information is in the frame..
                const frame = element.frame;
                const frameWindowLeft = (frame.marginLeft / 100.0) * element.width;
                const frameWindowTop = (frame.marginTop / 100.0) * element.height;
                const frameMarginRight = (frame.marginRight / 100.0) * element.width;
                const frameMarginBottom = (frame.marginBottom / 100.0) * element.height;

                // set frame window information
                freeObject.frameWindowX = frameWindowLeft;
                freeObject.frameWindowY = frameWindowTop;
                freeObject.frameWindowWidth = element.width - (frameWindowLeft + frameMarginRight);
                freeObject.frameWindowHeight = element.height - (frameWindowTop + frameMarginBottom);

                // set other data
                freeObject.edgeId = frame.id;
                freeObject.urlPrefix = frame.urlPrefix;
                freeObject.pathPrefix = frame.pathPrefix;

                this.copyImageData(frame, thumbSize.FULL, freeObject, 'fullFrame');
                this.copyImageData(frame, thumbSize.LARGE, freeObject, 'largeFrame');
                this.copyImageData(frame, thumbSize.MEDIUM, freeObject, 'mediumFrame');
                this.copyImageData(frame, thumbSize.SMALL, freeObject, 'smallFrame');
            } else {
                freeObject.frameWindowX = 0;
                freeObject.frameWindowY = 0;
                freeObject.frameWindowWidth = element.width;
                freeObject.frameWindowHeight = element.height;
            }
            // if we have a group
            if ((element as any).type && (element as any).type == 'group') {
                freeObject.type = 'group';
                const groupElement = (element as any);
                const innerObjects = (element as any)._objects;
                const innerFreeObjects:IFreeObject[] = [];
                for (let  i = 0; i < innerObjects.length; i++) {
                    let innerShape;
                    if (!innerObjects[i].type || (innerObjects[i].mainType && innerObjects[i].mainType == 'shapeAsset')) {
                        innerShape = globalImports.SF.Util.Translator.toShape(innerObjects[i]);
                    } else {
                        innerShape = innerObjects[i];
                    }

                    const innerElement = globalImports.SF.Util.Translator.toElement(innerShape);
                    const innerFreeObject = this.fromElement(innerElement);
                    innerFreeObjects.push(innerFreeObject);

                }

                for (const prop in groupElement) {
                    if (groupElement.hasOwnProperty(prop) && !freeObject.hasOwnProperty(prop)) {
                        freeObject[prop] = groupElement[prop];
                    }
                }
                freeObject._objects = innerFreeObjects;
            }

            // if we have a shape asset
            if ((element as any).mainType && (element as any).mainType === 'shapeAsset') {
                freeObject.mainType = 'shapeAsset';
                const shapeElement = (element as any);
                for (const prop in shapeElement) {
                    if (shapeElement.hasOwnProperty(prop) && !freeObject.hasOwnProperty(prop)) {
                        freeObject[prop] = shapeElement[prop];
                    }
                }

            }
            if ((element as any).mainType !== 'shapeAsset' && (element as any).type != 'group') {
                if (effects) {
                    imageEdits.brightness = effects.brightness;
                    imageEdits.contrast = effects.contrast;
                    imageEdits.saturation = effects.saturation;
                    imageEdits.luminance = effects.luminance;
                    imageEdits.hue = effects.hue;
                    imageEdits.red = effects.red;
                    imageEdits.green = effects.green;
                    imageEdits.blue = effects.blue;
                    imageEdits.sharpness = effects.sharpness;
                    imageEdits.flipHorizontal = effects.flipHorizontal;
                    imageEdits.flipVertical = effects.flipVertical;
                    imageEdits.rotation = imageEdits.exifRotation ? effects.rotation - imageEdits.exifRotation : (effects.rotation || 0);
                    imageEdits.initialRotation = effects.initialRotation || 0;
                    imageEdits.colorMode = this.getColorMode(effects.filter || '');

                    // deal with mask and border
                    if (effects.borderSize || effects.mask) {
                        freeObject.maskBorder = {
                            type: effects.mask ? masks.indexOf(masks.filter((key: string): boolean => {
                                return model.Masks[key] === effects.mask;
                            })[0]) : 0,
                            color: this.convertToDatabaseColor(effects.borderColor),
                            thickness: effects.borderSize
                        };
                    }

                    // vignette
                    if (effects.vignetteOpacity) {
                        // if color is in rgba format convert it to hex
                        if (effects.vignetteColor && effects.vignetteColor.indexOf('rgb') >= 0) {
                            freeObject.vignette = {
                                color: this.convertToDatabaseColor(globalImports.SF.Util.Translator.rgb2hex(effects.vignetteColor)),
                                alpha: globalImports.SF.Util.Translator.getOpacityFromRGBA(effects.vignetteColor),
                                blur: effects.vignetteBlur
                            };
                        } else {
                            freeObject.vignette = {
                                color: this.convertToDatabaseColor(effects.vignetteColor),
                                alpha: effects.vignetteOpacity / 100,
                                blur: effects.vignetteBlur
                            };
                        }
                    }

                    imageEdits.autoCorrect = effects.autoCorrect;
                    imageEdits.autoRedEye = effects.autoRedEye;
                    imageEdits.duotoneColor = effects.duotoneColor;
                    this.changeRedEyeOriginToTopLeft(imageEdits, element);
                }
            }
            // markers
            freeObject.isBackground = element instanceof model.BackgroundElement;
            freeObject.isForeground = element instanceof model.ForegroundElement;
            freeObject.isClipArt = element instanceof model.ClipartElement;
            if (element instanceof  model.BackgroundElement) {
                freeObject.onSpine = (element as IEditorBackgroundElement).onSpine;
            }
            if ((element as any).mainType !== 'shapeAsset' && (element as any).type != 'group') {
                freeObject.imageEdits = imageEdits;
            }
        }

        // deal with effects that can be applied on any element
        if (effects) {
            // glow, shadow, reflection
            if (effects.reflectionSize) {
                freeObject.glowShadowReflection = {
                    type: 3,
                    distance: effects.reflectionDistance,
                    falloff: effects.reflectionSize,
                    alpha: !effects.reflectionOpacity ? 0 : effects.reflectionOpacity / 100,
                    blur: effects.reflectionBlur
                };
            } else if (effects.shadowColor) {
                if (effects.shadowColor.indexOf('rgb') >= 0) {
                    freeObject.glowShadowReflection = {
                        type: 2,
                        color: this.convertToDatabaseColor(globalImports.SF.Util.Translator.rgb2hex(effects.shadowColor)),
                        alpha: globalImports.SF.Util.Translator.getOpacityFromRGBA(effects.shadowColor),
                        blur: effects.shadowBlur,
                        distance: effects.shadowDistance,
                        angle: effects.shadowAngle ? effects.shadowAngle - 180 : 0
                    };
                } else {
                    freeObject.glowShadowReflection = {
                        type: 2,
                        color: this.convertToDatabaseColor(effects.shadowColor),
                        alpha: effects.shadowOpacity ? effects.shadowOpacity / 100 : 0,
                        blur: effects.shadowBlur,
                        distance: effects.shadowDistance,
                        angle: effects.shadowAngle ? effects.shadowAngle - 180 : 0
                    };
                }
            } else if (effects.glowColor) {
                if (effects.glowColor.indexOf('rgb') >= 0) {
                    freeObject.glowShadowReflection = {
                        type: 1,
                        color: this.convertToDatabaseColor(globalImports.SF.Util.Translator.rgb2hex(effects.glowColor)),
                        alpha: globalImports.SF.Util.Translator.getOpacityFromRGBA(effects.glowColor),
                        blur: effects.glowBlur,
                        distance: 0,
                        angle: 0
                    };
                } else {
                    freeObject.glowShadowReflection = {
                        type: 1,
                        color: this.convertToDatabaseColor(effects.glowColor),
                        alpha: effects.glowOpacity ? effects.glowOpacity / 100 : 0,
                        blur: effects.glowBlur,
                        distance: 0,
                        angle: 0
                    };
                }
            }
            if ((element as any).mainType && (element as any).mainType === 'shapeAsset') {
                if (effects.hasOwnProperty('borderColor')) {
                    freeObject.border = {
                        color: this.convertToDatabaseColor(effects.borderColor),
                        size: effects.borderSize
                    };
                }

            }

        }

        return freeObject;
    }

    public fromErrors(pages: IEditorPage[]): IEditorElementError[] {
        return pages
            .map((page: IEditorPage): IEditorElementError[] => {
                return this.excludeEmptyTextErrorFromPage(page);
            })
            .reduce((a: IEditorElementError[], b: IEditorElementError[]): IEditorElementError[] => {
                return a.concat(b);
            });
    }

    /////////////////////
    // PRIVATE FUNCTIONS

    private getMargins(overlayImage:MaskOverlayImage, overlayMask:IMaskPreview ):void {
        overlayImage.margin_right = parseInt(overlayMask.margin_right as string || '0');
        overlayImage.margin_left = parseInt(overlayMask.margin_left as string || '0');
        overlayImage.margin_top = parseInt(overlayMask.margin_top as string || '0');
        overlayImage.margin_bottom = parseInt(overlayMask.margin_bottom as string || '0');
    }

    private convertToBleed(data: any, page: IEditorPage): IEditorEditsBleed|null {
        if (!data) {
            return null;
        }

        const bleed: any = {};

        bleed[productAttribute.BLEED_SIDE_LEFT] = data.leftSideDepth || data['bleed_side_l'] ? parseInt(data.leftSideDepth || data['bleed_side_l'], 10) : 0;
        bleed[productAttribute.BLEED_BACK_LEFT] = data.leftBackOverPrint || data['bleed_back_l'] ? parseInt(data.leftBackOverPrint || data['bleed_back_l'], 10) : 0;
        bleed[productAttribute.BLEED_SIDE_RIGHT] = data.rightSideDepth || data['bleed_side_r'] ? parseInt(data.rightSideDepth || data['bleed_side_r'], 10) : 0;
        bleed[productAttribute.BLEED_BACK_RIGHT] = data.rightBackOverPrint || data['bleed_back_r'] ? parseInt(data.rightBackOverPrint || data['bleed_back_r'], 10) : 0;
        bleed[productAttribute.BLEED_SIDE_TOP] = data.topSideDepth || data['bleed_side_t'] ? parseInt(data.topSideDepth || data['bleed_side_t'], 10) : 0;
        bleed[productAttribute.BLEED_BACK_TOP] = data.topBackOverPrint || data['bleed_back_t'] ? parseInt(data.topBackOverPrint || data['bleed_back_t'], 10) : 0;
        bleed[productAttribute.BLEED_SIDE_BOTTOM] = data.bottomSideDepth || data['bleed_side_b'] ? parseInt(data.bottomSideDepth || data['bleed_side_b'], 10) : 0;
        bleed[productAttribute.BLEED_BACK_BOTTOM] = data.bottomBackOverPrint || data['bleed_back_b'] ? parseInt(data.bottomBackOverPrint || data['bleed_back_b'], 10) : 0;

        if (page.type === model.PageTypes.FRONT_COVER || page.type === model.PageTypes.BACK_COVER || page.type === model.PageTypes.SPINE) {
            bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM] = data.coverPhotoMarginBottom || 0;
            bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP] = data.coverPhotoMarginTop || 0;
            bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT] = data.coverPhotoMarginLeft || 0;
            bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT] = data.coverPhotoMarginRight || 0;
        }

        bleed[model.WrapOptions.EDGE_STYLE] =
            data.hasOwnProperty('edgeStyle') ? data['edgeStyle'] : data.hasOwnProperty('edge_style') ? data['edge_style'] : null;
        bleed[model.WrapOptions.EDGE_COLOR] =
            data.hasOwnProperty('edgeColor') ? data['edgeColor'] : data.hasOwnProperty('edge_color') ? data['edge_color'] : null;
        bleed[model.WrapOptions.CORNER_TYPE] =
            data.hasOwnProperty('cornerType') ? data['cornerType'] : data.hasOwnProperty('corner_type') ? data['corner_type'] : null;
        bleed[model.WrapOptions.BACK_PRESENTATION] =
            data.hasOwnProperty('backPresentation') ? data['backPresentation'] : data.hasOwnProperty('back_presentation') ? data['back_presentation'] : null;

        bleed.width = page.width;
        bleed.height = page.height;

        return bleed;
    }

    private parseGlowShadowReflection(element: IEditorElement, glowShadowReflection: any): void {
        // type = {1 = glow, 2 = shadow, 3 = reflection)
        const effects: any = element.effects;

        if (glowShadowReflection.type == 1) {
            effects.glowColor = this.parseColor(glowShadowReflection.color);
            effects.glowOpacity = glowShadowReflection.alpha * 100;
            effects.glowBlur = glowShadowReflection.blur;
        } else if (glowShadowReflection.type == 2) {
            effects.shadowColor = this.parseColor(glowShadowReflection.color);
            effects.shadowOpacity = glowShadowReflection.alpha * 100;
            effects.shadowBlur = glowShadowReflection.blur;
            effects.shadowDistance = glowShadowReflection.distance;
            effects.shadowAngle = glowShadowReflection.angle + 180;
        } else if (glowShadowReflection.type == 3) {
            effects.reflectionDistance = glowShadowReflection.distance;
            effects.reflectionOpacity = glowShadowReflection.alpha * 100;
            effects.reflectionSize = glowShadowReflection.falloff;
            effects.reflectionBlur = glowShadowReflection.blur;
        }
    }

    private parseColor(color: number): string {
        //noinspection NonShortCircuitBooleanExpressionJS
        const hex = '00000' + (color || 0).toString(16);
        return '#' + hex.substring(hex.length - 6);
    }

    private parseMaskBorder(element: IEditorElement, maskBorder: IMaskBorder): void {
        const effects = element.effects;

        if (maskBorder.type !== 0) {
            effects.mask = model.Masks[masks[maskBorder.type]];

            // clean up data from flex
            if ((element as IEditorPhotoElement).frame) {
                (element as IEditorPhotoElement).frame = null;
            }
        }

        effects.borderColor = this.parseColor(maskBorder.color);
        effects.borderSize = maskBorder.thickness;
    }

    protected getImageThumbs(imageEdits: any): IThumbDictionary {
        const thumbs: IThumbDictionary = {};

        thumbs[thumbSize.FULL] = new model.Thumb(imageEdits.fullImageFilename, imageEdits.fullImageWidth, imageEdits.fullImageHeight);
        if (imageEdits.xlargeImageFilename && imageEdits.xlargeImageWidth && imageEdits.xlargeImageHeight) {
            thumbs[thumbSize.XLARGE] = new model.Thumb(imageEdits.xlargeImageFilename, imageEdits.xlargeImageWidth, imageEdits.xlargeImageHeight);
        }
        thumbs[thumbSize.LARGE] = new model.Thumb(imageEdits.largeImageFilename, imageEdits.largeImageWidth, imageEdits.largeImageHeight);
        thumbs[thumbSize.MEDIUM] = new model.Thumb(imageEdits.mediumImageFilename, imageEdits.mediumImageWidth, imageEdits.mediumImageHeight);
        thumbs[thumbSize.SMALL] = new model.Thumb(imageEdits.smallImageFilename, imageEdits.smallImageWidth, imageEdits.smallImageHeight);

        return thumbs;
    }

    // what is imageEdits/element's type!
    protected setFullImageDimension(imageEdits: any, element: any): void {
        // override when needed -- do nothing for photosite;
    }

    private getFrameThumbs(freeObject: any): IThumbDictionary {
        const thumbs: IThumbDictionary = {};

        thumbs[thumbSize.FULL] = new model.Thumb(freeObject.fullFrameFilename, freeObject.fullFrameWidth, freeObject.fullFrameHeight);
        if (freeObject.xlargeFrameFilename && freeObject.xlargeFrameWidth && freeObject.xlargeFrameHeight) {
            thumbs[thumbSize.XLARGE] = new model.Thumb(freeObject.xlargeFrameFilename, freeObject.xlargeFrameWidth, freeObject.xlargeFrameHeight);
        }
        thumbs[thumbSize.LARGE] = new model.Thumb(freeObject.largeFrameFilename, freeObject.largeFrameWidth, freeObject.largeFrameHeight);
        thumbs[thumbSize.MEDIUM] = new model.Thumb(freeObject.mediumFrameFilename, freeObject.mediumFrameWidth, freeObject.mediumFrameHeight);
        thumbs[thumbSize.SMALL] = new model.Thumb(freeObject.smallFrameFilename, freeObject.smallFrameWidth, freeObject.smallFrameHeight);

        return thumbs;
    }

    protected getImageModel(id: number|null, imageEdits: any): IEditorImage|null {
        if (!imageEdits || !imageEdits.fullImageFilename) {
            return null;
        }

        const res= new model.Image(id, imageEdits.urlPrefix, this.getImageThumbs(imageEdits));
        res.pathPrefix = imageEdits.pathPrefix;
        return res;

    }

    protected getPhotoModel(id: number, imageEdits: IImageEdits|undefined): IEditorPhoto|null {
        if (!imageEdits || !imageEdits.fullImageFilename) {
            return null;
        }

        const res = new model.Photo(id, imageEdits.urlPrefix, this.getImageThumbs(imageEdits));
        res.pathPrefix = imageEdits.pathPrefix;
        return res;
    }

    protected getBackgroundModel(id: number, imageEdits: IImageEdits|undefined): IEditorGraphicAsset|null {
        if (!imageEdits || !imageEdits.fullImageFilename) {
            return null;
        }

        const res = new model.GraphicAsset(id, 'background', imageEdits.urlPrefix, this.getImageThumbs(imageEdits));
        res.pathPrefix = imageEdits.pathPrefix;
        return res;
    }

    protected getClipArtModel(freeObject: IFreeObject): IEditorGraphicAsset|null {
        if (!freeObject || !freeObject.fullFrameFilename) {
            return null;
        }

        const res = new model.GraphicAsset(freeObject.graphicAssetId || 0, 'clipart', freeObject.urlPrefix || '', this.getFrameThumbs(freeObject));
        res.pathPrefix = freeObject.pathPrefix || '';
        return res;
    }

    private getEdgeAsImageModel(id: number, freeObject: IFreeObject): IEditorEdge|null {
        if (!freeObject || !freeObject.fullFrameFilename) {
            return null;
        }
        const res = new model.Edge(id,
            freeObject.urlPrefix || '',
            this.getFrameThumbs(freeObject),
            Math.round((freeObject.frameWindowY / freeObject.height) * 100),
            Math.round((freeObject.width - (freeObject.frameWindowWidth + freeObject.frameWindowX)) / freeObject.width * 100),
            Math.round((freeObject.height - (freeObject.frameWindowHeight + freeObject.frameWindowY)) / freeObject.height * 100),
            Math.round((freeObject.frameWindowX / freeObject.width) * 100)
        );

        res.pathPrefix = freeObject.pathPrefix || '';
        return res;
    }

    protected convertToDatabaseEdits(page: IEditorPage, canCreateArt: boolean): IDatabaseEdits {

        const filteredPageElements = this.filterDefaultTexts(page, canCreateArt);

        SF.Util.Helpers.revertCoverPageBleed(page);
        return {
            mode: page.mode,
            type: page.type,
            canEdit: page.canEdit,
            canReorder: page.canReorder,
            canDelete: page.canDelete,
            joinedPage: page.joined,
            width: page.width,
            height: page.height,
            freeObject: filteredPageElements.map((element:any) => this.fromElement(element)),
            bleed: this.convertToDatabaseBleed(page.bleed),
            visiblePages: [],
            elements: [],
            productType: page.productType,
        };
    }

    protected filterDefaultTexts(page:IEditorPage, canCreateArt: boolean): IEditorElement[] {
        // do nothing... should be overriden if needed
        return page.elements;
    }

    protected convertToDatabaseBleed(bleed: IEditorEditsBleed): IDatabaseBleed|null {
        if (!bleed) {
            return null;
        }

        const data: IDatabaseBleed = {};

        data.leftSideDepth = bleed[productAttribute.BLEED_SIDE_LEFT];
        data.leftBackOverPrint = bleed[productAttribute.BLEED_BACK_LEFT];
        data.rightSideDepth = bleed[productAttribute.BLEED_SIDE_RIGHT];
        data.rightBackOverPrint = bleed[productAttribute.BLEED_BACK_RIGHT];
        data.topSideDepth = bleed[productAttribute.BLEED_SIDE_TOP];
        data.topBackOverPrint = bleed[productAttribute.BLEED_BACK_TOP];
        data.bottomSideDepth = bleed[productAttribute.BLEED_SIDE_BOTTOM];
        data.bottomBackOverPrint = bleed[productAttribute.BLEED_BACK_BOTTOM];

        data.edgeStyle = bleed[model.WrapOptions.EDGE_STYLE] || null;
        data.edgeColor = bleed[model.WrapOptions.EDGE_COLOR] || null;
        data.cornerType = bleed[model.WrapOptions.CORNER_TYPE] || null;
        data.backPresentation = bleed[model.WrapOptions.BACK_PRESENTATION] || null;

        data.coverPhotoMarginTop = bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP] || null;
        data.coverPhotoMarginBottom = bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM] || null;
        data.coverPhotoMarginLeft = bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT] || null;
        data.coverPhotoMarginRight = bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT] || null;

        return data;
    }

    private convertToDatabaseTextStyles(styles: any[]):any[] {
        const transformedStyles:any = cloneDeep(styles);
        const lineKeys = Object.keys(transformedStyles);
        lineKeys.forEach((lineKey: string) => {
            if (transformedStyles[lineKey]) {
                const lineStyle = transformedStyles[lineKey];
                const keys = Object.keys(lineStyle);
                keys.forEach((key: string) => {
                    if (lineStyle[key]) {
                        const characterStyle = lineStyle[key];
                        if (Object.keys(characterStyle).length == 0) {
                            delete lineStyle[key];
                        }
                    }
                });
            }
        });

        return transformedStyles;
    }

    private convertToDatabaseColor(color: any): number {
        // if color is rgba convert it to hex
        if (color && color.indexOf('rgb') >= 0) {
            // convert from rgb to hex and keep opacity value
            color = globalImports.SF.Util.Translator.rgb2hex(color);
        }
        return parseInt(color ? color.substring(1) : 0, 16);
    }

    protected copyImageData(image: IEditorImage, size: string, object: any, fieldName: string): void {
        let thumb = image.getThumb(size);
        if (!thumb) {
            if (size === thumbSize.XLARGE) {
                return;
            }
            thumb = image.getThumb(thumbSize.MEDIUM);
            if (!thumb) {
                thumb = image.getThumb(thumbSize.FULL);
                if (!thumb) {
                    // if we are missing the full size, we are in big trouble...  but try the small?
                    thumb = image.getThumb(thumbSize.SMALL);
                    if (!thumb) {
                        // ok.  that's enough of that.  give up.  we tried.
                        return;
                    }
                }
            }
        }

        object[fieldName + 'Filename'] = thumb.name;
        object[fieldName + 'Width'] = thumb.width;
        object[fieldName + 'Height'] = thumb.height;
    }

    private getColorMode(filter: string|undefined): number {
        const idx = filter ? filters.indexOf(filter) : -1;
        return idx > 0 ? idx : 0;
    }

    private getFilter(colorMode: any): string|null {
        colorMode = parseInt(colorMode, 10);

        return colorMode && filters.length > colorMode ? filters[colorMode] : null;
    }

    private excludeEmptyTextErrorFromPage(page: IEditorPage): IEditorElementError[] {

        return page.errors.filter((error: IEditorElementError) => {
            return error.type != SF.Model.ElementErrorType.TEXT;
        });

    }

    private buildNonPrintablePreviews(previewThumb: IPreviewThumb []): IWebsiteImage {
        const width = previewThumb[0].width;
        const height= previewThumb[0].height;
        const thumbList: IWebsiteThumb[] = [];
        const fullUrl = previewThumb[0].url;
        for (let i = 0; i < previewThumb.length; i++) {
            const thumb:IWebsiteThumb = {
                url: previewThumb[i].url,
                width: previewThumb[i].width,
                height: previewThumb[i].height,
                thumb_type: previewThumb[i].thumb_type,
                file_path: previewThumb[i].file_path,
            };
            thumbList.push(thumb);
        }

        return {
            id: '',
            full_url: fullUrl,
            image_data: {},
            edits: {},
            width: width,
            height: height,
            thumb_list: thumbList,
            placeHolder: false,
        };
    }

    private buildOverlayMaskPreview(maskPreview: IMaskPreview): IWebsiteImage {
        return this.buildNonPrintablePreviews(maskPreview.thumbs);
    }

    private buildNonPrintableBackgroundPreview(nonPrintableBackgroundPreview: INonPrintableBackgroundPreview): IWebsiteImage {
        return this.buildNonPrintablePreviews(nonPrintableBackgroundPreview.thumbs);
    }

    private hasEditablePhoto(pageList: IPage[]):boolean {
        return pageList.some(page => {
           return (page.edits.canEdit && page.edits.freeObject.some((freeObject: { isBackground: any; isClipArt: any; canEdit: any; fixed: any; textEdits?: any; }) => {
               if (freeObject.isBackground || freeObject.isClipArt) {
                   // If the freeObject can be edited OR the freeObject is not super locked (user can unlock) then the can edit non-text...
                   return freeObject.canEdit || !freeObject.fixed;
               } else {
                   return !freeObject.textEdits;
               }
           }));
        });
    }

    //todo type for imageEdits and element?
    public changeRedEyeOriginToTopLeft(imageEdits: any, element: any): void {
        const effects: IEffects = element.effects;
        if (element.effects) {
            imageEdits.redEyes = effects.redEyes ? effects.redEyes.map((e: IPoint): IPoint => {
                e.x += element.image.width / 2;
                e.y += element.image.height / 2;

                return e;
            }) : [];
        }
    }

    public changeRedEyeOriginToCenter(imageEdits: IImageEdits, image:IEditorImage): IRedEye[] {
        return imageEdits.redEyes ? imageEdits.redEyes.map((e: IRedEye): IRedEye => {
            if (image) {
                e.x -= image.width / 2;
                e.y -= image.height / 2;
            }
            return e;
        }) : [];
    }

    public fixBeforeSave(pages: IEditorPage[]): void {
        for (let j = 0; j < pages.length; j++) {
            for (let k = 0; k < pages[j].elements.length; k++) {
                const element = pages[j].elements[k] as IEditorImageElement;

                // If there is a rotation, it means that the image position has been counter rotated when it was saved. We need to rotate it here again before updating the project data.
                if ((element instanceof SF.Model.PhotoElement || element instanceof SF.Model.BackgroundElement) && element.effects.rotation) {
                    SF.Util.Helpers.rotateImagePosition((element as IEditorPhotoElement).imagePosition, element.effects.rotation);
                }

                // If there is a red eye, the red eye point needs to be updated
                if (element.image && element.effects && element.effects.redEyes && element.effects.redEyes.length > 0) {
                    element.effects.redEyes = element.effects.redEyes.map(e => {
                        if (element.image) {
                            e.x -= element.image.width / 2;
                            e.y -= element.image.height / 2;
                        } else {
                            console.error('Image is missing from element');
                        }
                        return e;
                    });
                }
            }
            if ((pages[j].type === SF.Model.PageTypes.FRONT_COVER || pages[j].type === SF.Model.PageTypes.BACK_COVER || pages[j].type === SF.Model.PageTypes.SPINE) && pages[j].bleed) {
                pages[j].height += ((pages[j].bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM] || 0) + (pages[j].bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP] || 0));
                if (pages[j].type === SF.Model.PageTypes.FRONT_COVER) {
                    pages[j].width += pages[j].bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT] || 0;
                } else if (pages[j].type === SF.Model.PageTypes.BACK_COVER) {
                    pages[j].width += pages[j].bleed[coverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT] || 0;
                }
            }
        }
    }
}

SF.Util.Edits = new EditorEdits();
